/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1715731200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed May 15 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "mysql-destination",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to MySQL",
          "depth": 0,
          "id": "connect-to-mysql"
        }, {
          "value": "SSH tunneling",
          "depth": 1,
          "id": "ssh-tunneling"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Select table",
          "depth": 1,
          "id": "select-table"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Upsert query optimization",
          "depth": 1,
          "id": "upsert-query-optimization"
        }, {
          "value": "Delete behavior",
          "depth": 1,
          "id": "delete-behavior"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      img: "img"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "This destination combines the analytical power of your data warehouse with the low-latency performance of a transactional database."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Table"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to a MySQL table"
            }), _jsx(_components.td, {
              children: "Upsert"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-mysql",
        children: "Connect to MySQL"
      }), "\n", _jsx(WhitelistDetails, {
        target: "MySQL instance",
        privatelink: {
          supportedIntegrations: ["aws-privatelink", "gcp-psc"]
        }
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "MySQL"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Enter the following required fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Host"
          }), ": The hostname or IP address of your MySQL."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Port"
          }), ": The port number of your MySQL. The default is 3306, but yours may be different."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Database"
          }), ": The name of the database in your MySQL."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "User"
          }), ": This can be your personal MySQL login or a dedicated user for Hightouch."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Password"
          }), ": The password for the user specified above."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can optionally create or select a ", _jsx(_components.a, {
          href: "/security/ssh-tunneling",
          children: "previously configured tunnel"
        }), " or upload a ", _jsx(_components.code, {
          children: ".pem"
        }), " CA certificate."]
      }), "\n", _jsx(_components.h3, {
        id: "ssh-tunneling",
        children: "SSH tunneling"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch can connect directly to MySQL over the public internet or via an SSH tunnel. Since data is encrypted in transit via TLS, a direct connection is suitable for most use cases. You may need to set up a tunnel if your MySQL instance is on a private network or virtual private cloud (VPC)."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports both standard and reverse SSH tunnels. To learn more about SSH tunneling, refer to Hightouch's ", _jsx(_components.a, {
          href: "/security/ssh-tunneling",
          children: "tunneling documentation"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your MySQL destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the MySQL destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "select-table",
        children: "Select table"
      }), "\n", _jsx(_components.p, {
        children: "Select the existing table in MySQL you want to sync to."
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can match rows from your model to rows in MySQL on any column as long as it is a ", _jsx(_components.a, {
          href: "https://dev.mysql.com/doc/refman/8.0/en/partitioning-limitations-partitioning-keys-unique-keys.html",
          children: "primary key or unique key"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Select which model columns you want to sync to your MySQL table columns. Hightouch automatically pulls the columns from your table to make them available for you to map."
      }), "\n", _jsxs(Tip, {
        children: [_jsxs(_components.p, {
          children: ["When syncing to ", _jsx(_components.a, {
            href: "https://dev.mysql.com/doc/refman/8.0/en/binary-varbinary.html",
            children: "binary"
          }), " destination fields, Hightouch assumes the model column values to be ", _jsx(_components.a, {
            href: "https://en.wikipedia.org/wiki/Base64",
            children: "base64"
          }), " strings. If they aren't, you can use ", _jsx(_components.a, {
            href: "/syncs/mapping-data#template-mapping",
            children: "template mapping"
          }), " to ", _jsx(_components.a, {
            href: "/models/data-types-casting#casting",
            children: "cast"
          }), " them if needed."]
        }), _jsx(_components.p, {
          children: _jsx(_components.img, {
            src: "mapping-data/base-64-mapping.png",
            alt: "Base 64 template mapping"
          })
        })]
      }), "\n", _jsx(_components.h3, {
        id: "upsert-query-optimization",
        children: "Upsert query optimization"
      }), "\n", _jsxs(_components.p, {
        children: ["You can choose to use the ", _jsxs(_components.a, {
          href: "https://dev.mysql.com/doc/refman/8.0/en/server-system-variables.html#sysvar_local_infile",
          children: [_jsx(_components.code, {
            children: "LOAD DATA"
          }), " statement"]
        }), " in the upsert query during your sync. This option loads data from a text file into your database, which can be up to twenty times faster than the default ", _jsx(_components.code, {
          children: "INSERT INTO"
        }), " query Hightouch uses."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you enable the ", _jsx(_components.code, {
          children: "LOAD DATA"
        }), " option, Hightouch replaces databases rows with the mapped model row, if it exists. This means that if any MySQL fields aren't mapped in Hightouch they receive ", _jsx(_components.code, {
          children: "NULL"
        }), " values."]
      }), "\n", _jsxs(_components.p, {
        children: ["For Hightouch to successfully sync with the ", _jsx(_components.code, {
          children: "LOAD DATA"
        }), " option, you need to enable the ", _jsx(_components.a, {
          href: "https://dev.mysql.com/doc/refman/8.0/en/server-system-variables.html#sysvar_local_infile",
          children: _jsx(_components.code, {
            children: "local_infile"
          })
        }), " system variable."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Review the ", _jsx(_components.a, {
            href: "https://dev.mysql.com/doc/refman/8.0/en/load-data-local-security.html",
            children: "security considerations"
          }), " before enabling ", _jsx(_components.code, {
            children: "local_infile"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["When rows no longer shows up from your model's query results, you can configure the sync to ", _jsx(_components.strong, {
          children: "Do nothing"
        }), " or ", _jsx(_components.strong, {
          children: "Delete"
        }), " those rows from the MySQL."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the synced rows in the table in MySQL"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete"
              })
            }), _jsx(_components.td, {
              children: "Delete the synced rows from the table in MySQL"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
